import { useCallback, useEffect, useRef, useState } from 'react';
import type { HomepagePreview } from '@/api/repository/types/types';
import type { ClinicPhotoState } from './homepagePhotoRules';
import { overLimitCount, overLimitMessage } from './homepagePhotoRules';
import {
    HomepagePhotoEngine,
    HomepagePhotoMode,
    HomepagePhotoSnapshot,
    PhotoNotice,
} from './homepagePhotoEngine';

export { HOMEPAGE_PHOTO_BUSY_MESSAGE, HOMEPAGE_PHOTO_FULL_MESSAGE } from './homepagePhotoEngine';
export type { HomepagePhotoMode, PhotoNotice } from './homepagePhotoEngine';

/**
 * 홈페이지 대표 사진 자동 등록 — 가입 2단계(signup)·치과 정보 수정(edit)이 같이 쓴다(2026-10-26 승인 시안 + 리뷰 반영).
 * 흐름·규칙은 [HomepagePhotoEngine](React 밖, __checks__ 로 검사), 이 훅은 화면 상태로 이어 주기만 한다.
 */
interface Options {
    mode: HomepagePhotoMode;
    initialImages?: string[];
    initialUrl?: string | null;
    /** 지금 홈페이지 칸 값 */
    url: string;
    readPreview: (url: string) => Promise<{ result?: HomepagePreview; message?: string }>;
    importPhoto: (candidateUrl: string) => Promise<{ result?: { url: string }; message?: string }>;
    timeoutMs?: number;
}

export const useHomepagePhotoAuto = ({ mode, initialImages, initialUrl, url, readPreview, importPhoto, timeoutMs }: Options) => {
    // 칸 값·API 는 늘 최신 것을 쓰게 ref 로 넘긴다(엔진은 한 번만 만든다)
    const urlRef = useRef<string>(url);
    urlRef.current = url;
    const readRef = useRef(readPreview);
    readRef.current = readPreview;
    const importRef = useRef(importPhoto);
    importRef.current = importPhoto;

    const engineRef = useRef<HomepagePhotoEngine | null>(null);
    if (!engineRef.current) {
        engineRef.current = new HomepagePhotoEngine({
            mode,
            initialImages,
            initialUrl,
            timeoutMs,
            currentUrl: () => urlRef.current,
            readPreview: (u) => readRef.current(u),
            importPhoto: (c) => importRef.current(c),
        });
    }
    const engine: HomepagePhotoEngine = engineRef.current;
    const [snap, setSnap] = useState<HomepagePhotoSnapshot>(engine.snapshot);

    useEffect(() => {
        engine.subscribe(setSnap);
        setSnap(engine.snapshot);
        // 화면이 사라지면 늦게 온 결과는 버린다
        return () => engine.reset();
    }, [engine]);

    const readHomepage = useCallback((raw: string, options?: { force?: boolean }) => engine.readHomepage(raw, options), [engine]);
    const modify = useCallback((fn: (current: ClinicPhotoState) => ClinicPhotoState | null) => engine.modify(fn), [engine]);
    const setPhotoNotice = useCallback((text: string, tone?: PhotoNotice['tone']) => engine.setPhotoNotice(text, tone), [engine]);
    /** [저장]·[회원가입] 전 — 5장이 넘으면 사진 칸에 빨간 안내를 띄우고 그 문구를, 아니면 null */
    const limitError = useCallback((): string | null => {
        const over: number = overLimitCount(engine.snapshot.state);
        if (over === 0) return null;
        const text: string = overLimitMessage(over);
        engine.setPhotoNotice(text, 'error');
        return text;
    }, [engine]);

    const preview: HomepagePreview | null = snap.preview;
    const candidates: string[] = preview && !preview.failure ? preview.thumbnailCandidates : [];
    const previews: (string | null)[] = preview && !preview.failure ? preview.thumbnailPreviews ?? [] : [];

    return {
        mode,
        state: snap.state,
        modify,
        loading: snap.loading,
        /** 읽기·사진 저장 중 — [회원가입]·[저장]을 막는다 */
        busy: snap.loading || !!snap.pickingCandidate,
        preview,
        candidates,
        previews,
        message: snap.message,
        fieldMessage: snap.fieldMessage,
        photoNotice: snap.photoNotice,
        setPhotoNotice,
        pickingCandidate: snap.pickingCandidate,
        savedByCandidate: snap.savedByCandidate,
        readHomepage,
        /** 저장하려면 빼야 하는 장수(0 이면 저장 가능) */
        overLimit: overLimitCount(snap.state),
        limitError,
    };
};

export type HomepagePhotoController = ReturnType<typeof useHomepagePhotoAuto>;
